import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation } from "@apollo/client";
import Webcam from "react-webcam";
import Button from "@/components/Button";
import Dialog, { type Ref as DialogRef } from "@/components/Dialog";
import FileUpload from "@/components/FileUpload";
import Icon from "@/components/Icon";
import IconButton from "@/components/IconButton";
import Progress from "@/components/Progress";
import SegmentedButton from "@/components/SegmentedButton";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import {
  jpegFileFromDataUrl,
  snapshotWebcamDataUrl,
  stopWebcam,
  WebcamPreview,
  type WebcamFacingMode
} from "@/lib/webcamCamera";
import { showErrorSnackbar } from "@/lib/snackbarUtils";
import { SUMMARIZE_INTERACTION_NOTE_IMAGE_MUTATION } from "./mutations";

const PHOTO_ACCEPT = "image/*,.heic,.heif,.jpg,.jpeg,.png,.webp";
const DIALOG_HOST_CLASS = "handwriting-to-text-dialog";
const DIALOG_CLASS_DESKTOP = `${DIALOG_HOST_CLASS} !h-auto !w-[min(40rem,calc(100vw-3rem))] !min-w-0 !max-w-[min(40rem,calc(100vw-3rem))]`;
/** Above Add Interaction (1000) and Global Search compact sheet (1100). */
export const HANDWRITING_COMPACT_SHEET_Z_INDEX = 1200;
const handwritingDialogSheets = new WeakMap<Element, CSSStyleSheet>();

const DIALOG_SHADOW_CSS_DESKTOP = `
dialog {
  width: min(40rem, calc(100vw - 3rem)) !important;
  min-width: 0 !important;
  max-width: min(40rem, calc(100vw - 3rem)) !important;
  height: auto !important;
}
`;

function applyHandwritingDialogSize() {
  const host = document.querySelector(`md-dialog.${DIALOG_HOST_CLASS}`);
  const root = host?.shadowRoot;
  if (!host || !root || typeof CSSStyleSheet === "undefined" || !root.adoptedStyleSheets) {
    return;
  }

  let sheet = handwritingDialogSheets.get(host);
  if (!sheet) {
    try {
      sheet = new CSSStyleSheet();
      root.adoptedStyleSheets.push(sheet);
      handwritingDialogSheets.set(host, sheet);
    } catch {
      return;
    }
  }
  try {
    sheet.replaceSync(DIALOG_SHADOW_CSS_DESKTOP);
  } catch {
    // Lit dialog shadow roots are not always constructable in unit tests.
  }
}

export const SUMMARIZE_PHOTO_FAILED_MESSAGE = "We couldn't summarize that photo. Try another photo or type the notes.";
export const SUMMARIZE_PHOTO_API_UNAVAILABLE_MESSAGE =
  "This API can't summarize photos yet. Switch API backend to localhost, or type the notes.";
export const CAMERA_UNAVAILABLE_MESSAGE =
  "We couldn't open your camera. Allow camera permission, or switch to uploading a photo.";

export const PHOTO_SOURCE_CAMERA = "camera";
export const PHOTO_SOURCE_UPLOAD = "upload";

const PHOTO_SOURCE_OPTIONS = [
  { label: "Camera", value: PHOTO_SOURCE_CAMERA },
  { label: "Upload", value: PHOTO_SOURCE_UPLOAD }
];

type PhotoSource = typeof PHOTO_SOURCE_CAMERA | typeof PHOTO_SOURCE_UPLOAD;
type CameraStatus = "idle" | "loading" | "ready" | "error" | "denied";

function graphQLErrorText(error: unknown): string {
  if (!error || typeof error !== "object") return "";
  const err = error as {
    message?: string;
    graphQLErrors?: { message?: string }[];
    networkError?: { result?: { errors?: { message?: string }[] } };
  };
  const parts: string[] = [];
  if (typeof err.message === "string") parts.push(err.message);
  if (Array.isArray(err.graphQLErrors)) {
    parts.push(...err.graphQLErrors.map((item) => item.message ?? ""));
  }
  const networkErrors = err.networkError?.result?.errors;
  if (Array.isArray(networkErrors)) {
    parts.push(...networkErrors.map((item) => item.message ?? ""));
  }
  return parts.join(" ");
}

export function summarizePhotoFailureMessage(error: unknown): string {
  const text = graphQLErrorText(error);
  if (/Cannot query field|doesn't exist on type|Unknown field/i.test(text)) {
    return SUMMARIZE_PHOTO_API_UNAVAILABLE_MESSAGE;
  }
  return SUMMARIZE_PHOTO_FAILED_MESSAGE;
}

export default function TranscribeHandwrittenNotes({
  disabled,
  onBusyChange,
  onSummarized
}: {
  disabled?: boolean;
  onBusyChange?: (busy: boolean) => void;
  onSummarized: (text: string) => void;
}) {
  const { isCompactLayout } = useIsCompactLayout();
  const [isOpen, setIsOpen] = useState(false);
  const [photoSource, setPhotoSource] = useState<PhotoSource | null>(null);
  const resolvedSource: PhotoSource = photoSource ?? (isCompactLayout ? PHOTO_SOURCE_CAMERA : PHOTO_SOURCE_UPLOAD);
  const useCamera = resolvedSource === PHOTO_SOURCE_CAMERA;
  const [facingMode, setFacingMode] = useState<WebcamFacingMode>("environment");
  const [cameraStatus, setCameraStatus] = useState<CameraStatus>("idle");
  const [uploadError, setUploadError] = useState<string | undefined>();
  const [uploadKey, setUploadKey] = useState(0);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [capturedPreviewDataUrl, setCapturedPreviewDataUrl] = useState<string | null>(null);
  const cancelledRef = useRef(false);
  const dialogRef = useRef<DialogRef>(null);
  const webcamRef = useRef<Webcam>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [summarizeNoteImage] = useMutation(SUMMARIZE_INTERACTION_NOTE_IMAGE_MUTATION);

  const releaseCamera = useCallback(() => {
    stopWebcam(webcamRef.current, streamRef);
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
      releaseCamera();
      onBusyChange?.(false);
    };
  }, [onBusyChange, releaseCamera]);

  useEffect(() => {
    if (!isOpen || !useCamera) {
      releaseCamera();
      setCameraStatus(useCamera && isOpen ? "loading" : "idle");
    }
  }, [isOpen, releaseCamera, useCamera]);

  useEffect(() => {
    if (!isOpen || isCompactLayout) return;
    applyHandwritingDialogSize();
    const frame = requestAnimationFrame(() => applyHandwritingDialogSize());
    return () => cancelAnimationFrame(frame);
  }, [isCompactLayout, isOpen]);

  const handleUserMedia = useCallback((stream: MediaStream) => {
    if (streamRef.current && streamRef.current !== stream) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
    streamRef.current = stream;
    setCameraStatus("ready");
  }, []);

  const handleUserMediaError = useCallback((error: string | DOMException) => {
    setCameraStatus(error instanceof DOMException && error.name === "NotAllowedError" ? "denied" : "error");
  }, []);

  const resetFileInput = () => {
    setUploadKey((key) => key + 1);
  };

  const handleSourceChange = (value: PhotoSource) => {
    if (value === resolvedSource) return;
    setPhotoSource(value);
    setUploadError(undefined);
    setCapturedPreviewDataUrl(null);
    resetFileInput();
    if (value === PHOTO_SOURCE_CAMERA) {
      setCameraStatus("loading");
    } else {
      releaseCamera();
      setCameraStatus("idle");
    }
  };

  const failUpload = (message: string) => {
    setUploadError(message);
    showErrorSnackbar(message);
    resetFileInput();
  };

  const summarizeFile = async (file: File) => {
    setUploadError(undefined);
    setIsSummarizing(true);
    onBusyChange?.(true);
    try {
      const result = await summarizeNoteImage({ variables: { file } });
      if (cancelledRef.current) return;
      const payload = result.data?.summarizeInteractionNoteImage;
      const summary = payload?.summary?.trim();
      if (!payload?.success || !summary) {
        failUpload(payload?.message?.trim() || SUMMARIZE_PHOTO_FAILED_MESSAGE);
        return;
      }
      onSummarized(summary);
      resetFileInput();
      setIsOpen(false);
      releaseCamera();
      setCameraStatus("idle");
    } catch (error) {
      if (cancelledRef.current) return;
      failUpload(summarizePhotoFailureMessage(error));
    } finally {
      if (!cancelledRef.current) {
        setIsSummarizing(false);
        setCapturedPreviewDataUrl(null);
        onBusyChange?.(false);
      }
    }
  };

  const handleClosed = () => {
    if (isSummarizing) return;
    setIsOpen(false);
    setUploadError(undefined);
    setCapturedPreviewDataUrl(null);
    releaseCamera();
    setCameraStatus("idle");
    setFacingMode("environment");
  };

  const closeModal = () => {
    if (isSummarizing) return;
    if (dialogRef.current) {
      void dialogRef.current.close();
      return;
    }
    handleClosed();
  };

  const handleCapture = async () => {
    if (cameraStatus !== "ready" || isSummarizing || capturedPreviewDataUrl) return;
    const dataUrl = snapshotWebcamDataUrl(webcamRef.current, facingMode);
    if (!dataUrl) {
      failUpload(SUMMARIZE_PHOTO_FAILED_MESSAGE);
      return;
    }
    setCapturedPreviewDataUrl(dataUrl);
    releaseCamera();
    try {
      await summarizeFile(await jpegFileFromDataUrl(dataUrl));
    } catch {
      failUpload(SUMMARIZE_PHOTO_FAILED_MESSAGE);
    }
  };

  const handleSwitchCamera = () => {
    releaseCamera();
    setFacingMode((current) => (current === "environment" ? "user" : "environment"));
  };

  const busy = disabled || isSummarizing;
  const cameraFailed = cameraStatus === "error" || cameraStatus === "denied";
  const showingCapturedFrame = Boolean(capturedPreviewDataUrl);
  const canCapture = useCamera && cameraStatus === "ready" && !busy && !showingCapturedFrame;
  const canFlip = useCamera && cameraStatus === "ready" && !busy && !showingCapturedFrame;
  const previewMinHeight = isCompactLayout ? undefined : "22rem";
  const previewAreaClass = isCompactLayout ? "min-h-[12rem] flex-1" : "";

  const sourceToggle = (
    <SegmentedButton
      options={PHOTO_SOURCE_OPTIONS}
      value={resolvedSource}
      onChange={(value) => {
        if (value === PHOTO_SOURCE_CAMERA || value === PHOTO_SOURCE_UPLOAD) {
          handleSourceChange(value);
        }
      }}
      disabled={busy}
      fullWidth
      ariaLabel="Photo source"
    />
  );

  const captureActions = (
    <div className="flex w-full items-center gap-2">
      <div className="min-w-0 flex-1">
        <Button
          type="button"
          variant="filled"
          icon="photo_camera"
          ariaLabel="Capture"
          style={{ width: "100%" }}
          disabled={!canCapture}
          loading={isSummarizing}
          onClick={() => {
            void handleCapture();
          }}
          testId="handwriting-snapshot"
        >
          Capture
        </Button>
      </div>
      <IconButton
        name="cameraswitch"
        variant="square"
        skipMargin
        ariaLabel="Switch camera"
        disabled={!canFlip}
        onClick={handleSwitchCamera}
        data-testid="handwriting-switch-camera"
      />
    </div>
  );

  return (
    <>
      <Button
        type="button"
        variant="filledTonal"
        icon="photo_camera"
        ariaLabel="Capture My Notes"
        disabled={disabled}
        onClick={() => setIsOpen(true)}
        testId="handwriting-capture-open"
      >
        Capture My Notes
      </Button>
      {typeof document === "undefined"
        ? null
        : createPortal(
            <Dialog
              ref={dialogRef}
              open={isOpen}
              onClosed={handleClosed}
              disableClose={isSummarizing}
              hasFormElements={isCompactLayout}
              sheetZIndex={HANDWRITING_COMPACT_SHEET_Z_INDEX}
              className={DIALOG_CLASS_DESKTOP}
              header={{
                title: "Handwriting to text",
                action: (
                  <IconButton name="close" ariaLabel="close" skipMargin disabled={isSummarizing} onClick={closeModal} />
                )
              }}
              actions={useCamera ? captureActions : null}
            >
              <div
                className={isCompactLayout ? "flex min-h-0 w-full flex-1 flex-col gap-4" : "flex w-full flex-col gap-4"}
              >
                {!isCompactLayout && (
                  <p className="text-body-medium text-sys-brand-on-surface-variant">
                    Snap or upload a page of notes and get editable text.
                  </p>
                )}
                {sourceToggle}
                <div
                  className={`relative flex w-full items-center justify-center overflow-hidden rounded-xl bg-sys-brand-surface-container ${previewAreaClass}`}
                  style={previewMinHeight ? { minHeight: previewMinHeight } : undefined}
                >
                  {useCamera ? (
                    cameraFailed ? (
                      <div className="flex max-w-md flex-col items-center gap-6 px-6 py-12 text-center">
                        <Icon name="warning" size={32} className="text-sys-brand-on-surface-variant" />
                        <p className="text-body-medium text-sys-brand-on-surface-variant">
                          {CAMERA_UNAVAILABLE_MESSAGE}
                        </p>
                        <Button
                          type="button"
                          variant="outlined"
                          disabled={busy}
                          onClick={() => handleSourceChange(PHOTO_SOURCE_UPLOAD)}
                        >
                          Upload a photo
                        </Button>
                      </div>
                    ) : showingCapturedFrame ? (
                      <>
                        <img
                          src={capturedPreviewDataUrl ?? undefined}
                          alt="Captured notes"
                          className="h-full min-h-full w-full object-contain"
                          data-testid="handwriting-captured-preview"
                        />
                        {isSummarizing && (
                          <div
                            className="absolute inset-0 flex items-center justify-center bg-sys-brand-scrim/40"
                            data-testid="handwriting-capture-processing"
                          >
                            <Progress />
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {cameraStatus !== "ready" && <Progress />}
                        {isOpen && (
                          <WebcamPreview
                            ref={webcamRef}
                            facingMode={facingMode}
                            onUserMedia={handleUserMedia}
                            onUserMediaError={handleUserMediaError}
                            className={cameraStatus === "ready" ? "h-full min-h-full w-full object-cover" : "hidden"}
                            data-testid="handwriting-webcam"
                          />
                        )}
                      </>
                    )
                  ) : (
                    <div className="w-full px-4 py-2 sm:max-w-lg sm:px-6">
                      <FileUpload
                        key={`${uploadKey}-${resolvedSource}`}
                        label="Handwritten Notes"
                        buttonText="Choose Photo"
                        icon="upload"
                        subtitle="Upload a photo of your notes to summarize them."
                        accept={PHOTO_ACCEPT}
                        maxSizeMB={10}
                        loading={isSummarizing}
                        disabled={busy}
                        errorMsg={uploadError}
                        buttonOnly={isCompactLayout}
                        handleUpload={(file) => {
                          void summarizeFile(file);
                        }}
                        onClear={() => setUploadError(undefined)}
                        onError={(error) => {
                          setUploadError(error);
                          resetFileInput();
                        }}
                        variant="filledTonal"
                      />
                    </div>
                  )}
                </div>
              </div>
            </Dialog>,
            document.body
          )}
    </>
  );
}
